import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOffline } from '../../context/OfflineContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { BookOpen, Plus, Tag, Trash2, Edit, Loader2, Search, X, CloudOff, Camera } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import PhotoDiagnosis from './PhotoDiagnosis';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FarmNotebook = () => {
  const { getAuthHeader } = useAuth();
  const { isOnline, queueOfflineAction, cacheData, getCachedData } = useOffline();
  const [notes, setNotes] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', tags: '' });
  const [saving, setSaving] = useState(false);

  const fetchNotes = async (tag = null) => {
    setLoading(true);
    try {
      const params = tag ? { tag } : {};
      const res = await axios.get(`${API_URL}/api/notes`, {
        headers: getAuthHeader(),
        params
      });
      setNotes(res.data.notes || []);
      // Cache notes for offline access
      cacheData('farm_notes', res.data.notes || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      // Try to load from cache
      const cached = getCachedData('farm_notes');
      if (cached) {
        setNotes(cached);
        toast.info('Showing cached notes');
      } else {
        toast.error('Failed to load notes');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notes/tags/list`, {
        headers: getAuthHeader()
      });
      setTags(res.data.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  useEffect(() => {
    fetchNotes();
    fetchTags();
  }, []);

  const handleTagFilter = (tagName) => {
    if (selectedTag === tagName) {
      setSelectedTag(null);
      fetchNotes();
    } else {
      setSelectedTag(tagName);
      fetchNotes(tagName);
    }
  };

  const openNewNote = () => {
    setEditingNote(null);
    setFormData({ title: '', content: '', tags: '' });
    setIsDialogOpen(true);
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setFormData({
      title: note.title,
      content: note.content,
      tags: note.tags.join(', ')
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    const tagsArray = formData.tags
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(t => t);

    const noteData = { title: formData.title, content: formData.content, tags: tagsArray };

    try {
      let newNote;
      if (editingNote) {
        const res = await axios.put(
          `${API_URL}/api/notes/${editingNote.id}`,
          noteData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
        );
        newNote = res.data.note || { ...editingNote, ...noteData };
        toast.success('Note updated');
        // Update in place for editing
        setNotes(prev => prev.map(n => n.id === editingNote.id ? { ...n, ...noteData } : n));
      } else {
        const res = await axios.post(
          `${API_URL}/api/notes`,
          noteData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
        );
        newNote = res.data.note || { 
          id: res.data.id || `note_${Date.now()}`, 
          ...noteData, 
          created_at: new Date().toISOString() 
        };
        toast.success('Note created');
        // Optimistically add the new note to the top of the list
        setNotes(prev => [newNote, ...prev]);
      }
      
      setIsDialogOpen(false);
      setFormData({ title: '', content: '', tags: '' });
      // Refresh tags in background
      fetchTags();
    } catch (error) {
      console.error('Failed to save note:', error);
      // Queue for offline sync if network error
      if (!isOnline || error.message?.includes('Network')) {
        const offlineNote = {
          id: `offline_${Date.now()}`,
          ...noteData,
          created_at: new Date().toISOString(),
          _offline: true
        };
        
        // Add to local notes immediately
        setNotes(prev => [offlineNote, ...prev]);
        
        // Queue the action for sync
        queueOfflineAction({
          url: editingNote 
            ? `${API_URL}/api/notes/${editingNote.id}` 
            : `${API_URL}/api/notes`,
          method: editingNote ? 'PUT' : 'POST',
          body: noteData
        });
        
        setIsDialogOpen(false);
        toast.success('Note saved offline', { 
          description: 'Will sync when you reconnect',
          icon: <CloudOff className="w-4 h-4" />
        });
      } else {
        toast.error('Failed to save note');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/notes/${noteId}`, {
        headers: getAuthHeader()
      });
      toast.success('Note deleted');
      fetchNotes(selectedTag);
      fetchTags();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  const filteredNotes = notes.filter(note =>
    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="farm-notebook">
      <Tabs defaultValue="notes" className="w-full">
        <TabsList className="grid grid-cols-2 mb-6">
          <TabsTrigger value="notes" data-testid="notebook-tab-notes">
            <BookOpen className="w-4 h-4 mr-2" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="diagnose" data-testid="notebook-tab-diagnose">
            <Camera className="w-4 h-4 mr-2" />
            Diagnose
          </TabsTrigger>
        </TabsList>

        <TabsContent value="diagnose" className="space-y-4">
          <PhotoDiagnosis />
        </TabsContent>

        <TabsContent value="notes" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Farm Notebook
          </h1>
          <p className="text-muted-foreground mt-1">
            Keep track of observations and activities
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" onClick={openNewNote} data-testid="new-note-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Note
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingNote ? 'Edit Note' : 'New Note'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Fence repair needed"
                  className="input-farm"
                  data-testid="note-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Write your observations..."
                  className="min-h-[150px] resize-none"
                  data-testid="note-content-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (comma separated)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g., livestock, fencing, urgent"
                  className="input-farm"
                  data-testid="note-tags-input"
                />
              </div>
              <Button 
                onClick={handleSave} 
                className="btn-primary w-full" 
                disabled={saving}
                data-testid="save-note-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingNote ? 'Update Note' : 'Create Note'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Tags */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search notes..."
            className="input-farm pl-10"
            data-testid="search-notes-input"
          />
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Badge
                key={tag.name}
                variant={selectedTag === tag.name ? "default" : "secondary"}
                className={`cursor-pointer transition-colors ${
                  selectedTag === tag.name 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted'
                }`}
                onClick={() => handleTagFilter(tag.name)}
                data-testid={`tag-filter-${tag.name}`}
              >
                <Tag className="w-3 h-3 mr-1" />
                {tag.name}
                <span className="ml-1 text-xs opacity-70">({tag.count})</span>
                {selectedTag === tag.name && (
                  <X className="w-3 h-3 ml-1" />
                )}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Notes Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredNotes.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredNotes.map((note, index) => (
            <Card 
              key={note.id} 
              className={`card-farm animate-fade-in stagger-${(index % 5) + 1}`}
              data-testid={`note-card-${note.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="font-heading text-base line-clamp-1">
                    {note.title}
                  </CardTitle>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {formatDate(note.created_at)}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {note.content}
                </p>
                {note.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {note.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 pt-2 border-t border-border/60">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditNote(note)}
                    className="flex-1"
                    data-testid={`edit-note-${note.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(note.id)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    data-testid={`delete-note-${note.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-farm">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold text-foreground mb-2">
              {searchTerm || selectedTag ? 'No notes found' : 'Start your notebook'}
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              {searchTerm || selectedTag 
                ? 'Try adjusting your search or filters' 
                : 'Keep track of observations, tasks, and farm activities'}
            </p>
            {!searchTerm && !selectedTag && (
              <Button onClick={openNewNote} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Create First Note
              </Button>
            )}
          </CardContent>
        </Card>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FarmNotebook;
